import { RoomSession } from './Room'
import {
  ADVISOR_REFERRAL_PRESET_IDS,
  DECISION_TRACKER_PRESET_IDS,
  FUTURE_MEETINGS_PRESET_IDS,
  LIST_OF_LIFE_EVENTS_PRESET_IDS,
  NON_FINANCIAL_PERSONAL_INFORMATION_PRESET_IDS,
} from '../views/MeetingSummary/CRM/PresetMappings'
import { IntegrationEmail } from './Email'
import { MentionData } from '../apps/professional-app/fa/MeetingSummary/MeetingResults/Lexical/Nodes/MentionNode/MentionNode'
import { SyncStatus } from '../apps/professional-app/fa/MeetingSummary/ExternalUpdates/models'

const env = import.meta.env.REACT_APP_ENV! as 'prod' | 'staging' | 'dev'

export const BusinessEventSourceType = { AI_FEATURE_EXECUTION: 'aiFeatureExecution', EMAIL: 'email' } as const

export type BusinessEventSourceTypeValues = (typeof BusinessEventSourceType)[keyof typeof BusinessEventSourceType]

export interface AIFeatureExecutionBusinessEventSource {
  type: typeof BusinessEventSourceType.AI_FEATURE_EXECUTION
  roomId: string
  sessionId: string
  aiFeatureId: string
}

export interface EmailBusinessEventSource {
  type: typeof BusinessEventSourceType.EMAIL
  emailConnector: string
  threadId: string
  emailId: string
}

export type BusinessEventSource = AIFeatureExecutionBusinessEventSource | EmailBusinessEventSource

export const BUSINESS_EVENT_SOURCE_ICON_MAPPING: Record<BusinessEventSourceTypeValues, string> = {
  [BusinessEventSourceType.AI_FEATURE_EXECUTION]: 'bi-calendar',
  [BusinessEventSourceType.EMAIL]: 'bi-envelope',
}

export type BusinessEventDetailsValueType = string | number | boolean | null | string[]

export const BusinessEventType = {
  REFERRAL: 'referral',
  MARRIAGE: 'marriage',
  WIDOWING: 'widowing',
  DIVORCE: 'divorce',
  CHILD_BIRTH: 'childBirth',
  PROPERTY_PURCHASE: 'propertyPurchase',
  PROPERTY_SALE: 'propertySale',
  JOB_CHANGE: 'jobChange',
  RETIREMENT: 'retirement',
  INHERITANCE: 'inheritance',
  BUSINESS_START: 'businessStart',
  HEALTH_ISSUE: 'healthIssue',
  FAMILY_MEMBER_HEALTH_ISSUE: 'familyMemberHealthIssue',
  CHILDREN_STARTING_COLLEGE: 'childrenStartingCollege',
  DECISION_MADE: 'decisionMade',
  FUTURE_MEETING: 'futureMeeting',
  HOBBY: 'hobby',
  INTEREST: 'interest',
  MEMBERSHIP: 'membership',
  RELIGION: 'religion',
  TRAVELING: 'traveling',
  MOVING: 'moving',
  PET: 'pet',
} as const

export type BusinessEventType = (typeof BusinessEventType)[keyof typeof BusinessEventType]

export interface BusinessEvent {
  id: string
  type: BusinessEventType
  details: Record<string, BusinessEventDetailsValueType>
  source: BusinessEventSource
  at: string | null
  createdAt: string
  syncStatus: SyncStatus[]
}

export const BUSINESS_EVENT_LABEL_MAP: Record<BusinessEventType, string> = {
  [BusinessEventType.REFERRAL]: 'Referral',
  [BusinessEventType.MARRIAGE]: 'Marriage',
  [BusinessEventType.WIDOWING]: 'Widowed',
  [BusinessEventType.DIVORCE]: 'Divorce',
  [BusinessEventType.CHILD_BIRTH]: 'Birth of a Child',
  [BusinessEventType.PROPERTY_PURCHASE]: 'Bought property',
  [BusinessEventType.PROPERTY_SALE]: 'Sold property',
  [BusinessEventType.JOB_CHANGE]: 'Job change',
  [BusinessEventType.RETIREMENT]: 'Retirement',
  [BusinessEventType.INHERITANCE]: 'Received Inheritance',
  [BusinessEventType.BUSINESS_START]: 'Started a new Business',
  [BusinessEventType.HEALTH_ISSUE]: 'Major Health Issue',
  [BusinessEventType.FAMILY_MEMBER_HEALTH_ISSUE]: 'Family Member Health Issue',
  [BusinessEventType.CHILDREN_STARTING_COLLEGE]: 'Children Starting College',
  [BusinessEventType.DECISION_MADE]: 'Decision made',
  [BusinessEventType.FUTURE_MEETING]: 'Future Meeting',
  [BusinessEventType.INTEREST]: 'Interest detected',
  [BusinessEventType.HOBBY]: 'Hobby detected',
  [BusinessEventType.MEMBERSHIP]: 'Membership detected',
  [BusinessEventType.RELIGION]: 'Religion detected',
  [BusinessEventType.TRAVELING]: 'Traveling',
  [BusinessEventType.MOVING]: 'Moving / relocation',
  [BusinessEventType.PET]: 'Pet detected',
}

export interface BaseResolvedBusinessEventSource {
  sourceKey: string
}

export interface AIFeatureExecutionResolvedBusinessEventSource extends BaseResolvedBusinessEventSource {
  type: typeof BusinessEventSourceType.AI_FEATURE_EXECUTION
  source: RoomSession
}

export interface EmailResolvedBusinessEventSource extends BaseResolvedBusinessEventSource {
  type: typeof BusinessEventSourceType.EMAIL
  source: IntegrationEmail
}

export type ResolvedBusinessEventSource = AIFeatureExecutionResolvedBusinessEventSource | EmailResolvedBusinessEventSource

export const BUSINESS_EVENT_TYPES_PER_PRESET_ID_MAPPING: { [key: string]: BusinessEventType[] } = {
  [FUTURE_MEETINGS_PRESET_IDS[env]]: [BusinessEventType.FUTURE_MEETING],
  [DECISION_TRACKER_PRESET_IDS[env]]: [BusinessEventType.DECISION_MADE],
  [LIST_OF_LIFE_EVENTS_PRESET_IDS[env]]: [
    BusinessEventType.MARRIAGE,
    BusinessEventType.DIVORCE,
    BusinessEventType.WIDOWING,
    BusinessEventType.CHILD_BIRTH,
    BusinessEventType.PROPERTY_PURCHASE,
    BusinessEventType.PROPERTY_SALE,
    BusinessEventType.JOB_CHANGE,
    BusinessEventType.INHERITANCE,
    BusinessEventType.BUSINESS_START,
    BusinessEventType.HEALTH_ISSUE,
    BusinessEventType.FAMILY_MEMBER_HEALTH_ISSUE,
    BusinessEventType.CHILDREN_STARTING_COLLEGE,
    BusinessEventType.RETIREMENT,
  ],
  [ADVISOR_REFERRAL_PRESET_IDS[env]]: [BusinessEventType.REFERRAL],
  [NON_FINANCIAL_PERSONAL_INFORMATION_PRESET_IDS[env]]: [
    BusinessEventType.HOBBY,
    BusinessEventType.INTEREST,
    BusinessEventType.MEMBERSHIP,
    BusinessEventType.RELIGION,
    BusinessEventType.TRAVELING,
    BusinessEventType.MOVING,
    BusinessEventType.PET,
  ],
}

export const BusinessEventGroup = {
  LEAD_MANAGEMENT: 'Lead management',
  LIFE_AND_BUSINESS_EVENTS: 'Life & business events',
  DECISIONS: 'Decisions',
  ADDITIONAL_SIGNALS: 'Additional signals',
} as const

export type BusinessEventGroupType = (typeof BusinessEventGroup)[keyof typeof BusinessEventGroup]

export const BUSINESS_EVENT_GROUP_MAPPING: Record<BusinessEventGroupType, BusinessEventType[]> = {
  [BusinessEventGroup.LEAD_MANAGEMENT]: [BusinessEventType.REFERRAL],
  [BusinessEventGroup.LIFE_AND_BUSINESS_EVENTS]: [
    BusinessEventType.MARRIAGE,
    BusinessEventType.DIVORCE,
    BusinessEventType.WIDOWING,
    BusinessEventType.CHILD_BIRTH,
    BusinessEventType.PROPERTY_PURCHASE,
    BusinessEventType.PROPERTY_SALE,
    BusinessEventType.JOB_CHANGE,
    BusinessEventType.INHERITANCE,
    BusinessEventType.BUSINESS_START,
    BusinessEventType.HEALTH_ISSUE,
    BusinessEventType.FAMILY_MEMBER_HEALTH_ISSUE,
    BusinessEventType.CHILDREN_STARTING_COLLEGE,
    BusinessEventType.RETIREMENT,
  ],
  [BusinessEventGroup.DECISIONS]: [BusinessEventType.DECISION_MADE],
  [BusinessEventGroup.ADDITIONAL_SIGNALS]: [
    BusinessEventType.FUTURE_MEETING,
    BusinessEventType.HOBBY,
    BusinessEventType.INTEREST,
    BusinessEventType.MEMBERSHIP,
    BusinessEventType.RELIGION,
    BusinessEventType.TRAVELING,
    BusinessEventType.MOVING,
    BusinessEventType.PET,
  ],
}

export const BUSINESS_EVENT_DETAILS_KEYS_MAPPING: Partial<Record<BusinessEventType, string[]>> = {
  [BusinessEventType.DECISION_MADE]: ['decision', 'agreedToDo'],
}

const COMMON_BUSINESS_EVENT_DETAILS = [{ id: 'at', label: 'Business Event at', metadata: { type: 'date', descriptorType: 'variable' } }] satisfies MentionData[]

const withCommonDetails = <T extends Record<BusinessEventType, MentionData[]>>(mapping: T): T =>
  Object.fromEntries(Object.entries(mapping).map(([key, value]) => [key, [...value, ...COMMON_BUSINESS_EVENT_DETAILS]])) as T

export const BUSINESS_EVENT_DETAILS_MAPPING = withCommonDetails({
  [BusinessEventType.REFERRAL]: [
    { id: 'referredId', label: 'Referred ID', metadata: { type: 'string', descriptorType: 'field' } },
    { id: 'refereeName', label: 'Referee Name', metadata: { type: 'string', descriptorType: 'field' } },
    { id: 'refereeEmail', label: 'Referee Email', metadata: { type: 'string', descriptorType: 'field' } },
    { id: 'alreadyHappened', label: 'Already Happened', metadata: { type: 'boolean', descriptorType: 'field' } },
  ],

  [BusinessEventType.MARRIAGE]: [
    { id: 'partnerName', label: 'Partner Name', metadata: { type: 'string', descriptorType: 'field' } },
    { id: 'lastNameChange', label: 'Last Name Change', metadata: { type: 'string', descriptorType: 'field' } },
  ],

  [BusinessEventType.WIDOWING]: [
    { id: 'partnerName', label: 'Partner Name', metadata: { type: 'string', descriptorType: 'field' } },
    { id: 'lastNameChange', label: 'Last Name Change', metadata: { type: 'string', descriptorType: 'field' } },
  ],

  [BusinessEventType.DIVORCE]: [
    { id: 'partnerName', label: 'Partner Name', metadata: { type: 'string', descriptorType: 'field' } },
    { id: 'lastNameChange', label: 'Last Name Change', metadata: { type: 'string', descriptorType: 'field' } },
  ],

  [BusinessEventType.CHILD_BIRTH]: [
    { id: 'childName', label: 'Child Name', metadata: { type: 'string', descriptorType: 'field' } },
    { id: 'childGender', label: 'Child Gender', metadata: { type: 'string', descriptorType: 'field' } },
  ],

  [BusinessEventType.PROPERTY_PURCHASE]: [
    { id: 'propertyType', label: 'Property Type', metadata: { type: 'string', descriptorType: 'field' } },
    { id: 'propertyValue', label: 'Property Value', metadata: { type: 'number', descriptorType: 'field' } },
  ],

  [BusinessEventType.PROPERTY_SALE]: [
    { id: 'propertyType', label: 'Property Type', metadata: { type: 'string', descriptorType: 'field' } },
    { id: 'propertyValue', label: 'Property Value', metadata: { type: 'number', descriptorType: 'field' } },
  ],

  [BusinessEventType.JOB_CHANGE]: [
    { id: 'employerName', label: 'Employer Name', metadata: { type: 'string', descriptorType: 'field' } },
    { id: 'jobTitle', label: 'Job Title', metadata: { type: 'string', descriptorType: 'field' } },
  ],

  [BusinessEventType.RETIREMENT]: [
    { id: 'retirementAge', label: 'Retirement Age', metadata: { type: 'number', descriptorType: 'field' } },
    { id: 'retirementIncomeSource', label: 'Income Source', metadata: { type: 'string', descriptorType: 'field' } },
    { id: 'retiredFrom', label: 'Retired From', metadata: { type: 'string', descriptorType: 'field' } },
    { id: 'pensionDetails', label: 'Pension Details', metadata: { type: 'string', descriptorType: 'field' } },
  ],

  [BusinessEventType.INHERITANCE]: [
    { id: 'amount', label: 'Amount', metadata: { type: 'number', descriptorType: 'field' } },
    { id: 'source', label: 'Source', metadata: { type: 'string', descriptorType: 'field' } },
    { id: 'assetType', label: 'Asset Type', metadata: { type: 'string', descriptorType: 'field' } },
  ],

  [BusinessEventType.BUSINESS_START]: [
    { id: 'businessName', label: 'Business Name', metadata: { type: 'string', descriptorType: 'field' } },
    { id: 'businessType', label: 'Business Type', metadata: { type: 'string', descriptorType: 'field' } },
    { id: 'industry', label: 'Industry', metadata: { type: 'string', descriptorType: 'field' } },
    { id: 'sharePercent', label: 'Ownership %', metadata: { type: 'number', descriptorType: 'field' } },
  ],

  [BusinessEventType.HEALTH_ISSUE]: [
    { id: 'healthIssueType', label: 'Health Issue Type', metadata: { type: 'string', descriptorType: 'field' } },
    { id: 'recoveryStatus', label: 'Recovery Status', metadata: { type: 'string', descriptorType: 'field' } },
    { id: 'insuranceClaimFiled', label: 'Insurance Claim Filed', metadata: { type: 'boolean', descriptorType: 'field' } },
    { id: 'healthcareProvider', label: 'Healthcare Provider', metadata: { type: 'string', descriptorType: 'field' } },
  ],

  [BusinessEventType.FAMILY_MEMBER_HEALTH_ISSUE]: [
    { id: 'relativeName', label: 'Relative Name', metadata: { type: 'string', descriptorType: 'field' } },
    { id: 'relation', label: 'Relation', metadata: { type: 'string', descriptorType: 'field' } },
    { id: 'healthIssueType', label: 'Health Issue Type', metadata: { type: 'string', descriptorType: 'field' } },
    { id: 'needsCare', label: 'Needs Care', metadata: { type: 'boolean', descriptorType: 'field' } },
  ],

  [BusinessEventType.CHILDREN_STARTING_COLLEGE]: [
    { id: 'childName', label: 'Child Name', metadata: { type: 'string', descriptorType: 'field' } },
    { id: 'collegeName', label: 'College Name', metadata: { type: 'string', descriptorType: 'field' } },
    { id: 'tuitionAmount', label: 'Tuition Amount', metadata: { type: 'number', descriptorType: 'field' } },
    { id: 'financialAidStatus', label: 'Financial Aid Status', metadata: { type: 'string', descriptorType: 'field' } },
  ],

  [BusinessEventType.DECISION_MADE]: [
    { id: 'decision', label: 'Decision', metadata: { type: 'string', descriptorType: 'field' } },
    { id: 'actorIds', label: 'Actor IDs', metadata: { type: 'string[]', descriptorType: 'field' } },
    { id: 'agreedToDo', label: 'Agreed To Do', metadata: { type: 'boolean', descriptorType: 'field' } },
  ],

  [BusinessEventType.FUTURE_MEETING]: [{ id: 'meetingTitle', label: 'Meeting Title', metadata: { type: 'string', descriptorType: 'field' } }],

  [BusinessEventType.HOBBY]: [
    { id: 'hobbyName', label: 'Hobby Name', metadata: { type: 'string', descriptorType: 'field' } },
    { id: 'hobbyType', label: 'Hobby Type', metadata: { type: 'string', descriptorType: 'field' } },
  ],

  [BusinessEventType.INTEREST]: [
    { id: 'interestName', label: 'Interest Name', metadata: { type: 'string', descriptorType: 'field' } },
    { id: 'interestType', label: 'Interest Type', metadata: { type: 'string', descriptorType: 'field' } },
  ],

  [BusinessEventType.MEMBERSHIP]: [
    { id: 'groupName', label: 'Group Name', metadata: { type: 'string', descriptorType: 'field' } },
    { id: 'groupType', label: 'Group Type', metadata: { type: 'string', descriptorType: 'field' } },
  ],

  [BusinessEventType.RELIGION]: [
    { id: 'denomination', label: 'Denomination', metadata: { type: 'string', descriptorType: 'field' } },
    { id: 'communityName', label: 'Community Name', metadata: { type: 'string', descriptorType: 'field' } },
  ],

  [BusinessEventType.TRAVELING]: [
    { id: 'travelLocation', label: 'Location', metadata: { type: 'string', descriptorType: 'field' } },
    { id: 'travelDurationDays', label: 'Duration (days)', metadata: { type: 'number', descriptorType: 'field' } },
    { id: 'travelType', label: 'Travel Type', metadata: { type: 'string', descriptorType: 'field' } },
    { id: 'travelPattern', label: 'Travel Pattern', metadata: { type: 'string', descriptorType: 'field' } },
    { id: 'frequency', label: 'Frequency', metadata: { type: 'number', descriptorType: 'field' } },
  ],

  [BusinessEventType.MOVING]: [
    { id: 'previousLocation', label: 'Previous Location', metadata: { type: 'string', descriptorType: 'field' } },
    { id: 'newLocation', label: 'New Location', metadata: { type: 'string', descriptorType: 'field' } },
  ],

  [BusinessEventType.PET]: [
    { id: 'petSpecies', label: 'Species', metadata: { type: 'string', descriptorType: 'field' } },
    { id: 'count', label: 'Count', metadata: { type: 'number', descriptorType: 'field' } },
    { id: 'names', label: 'Names', metadata: { type: 'string[]', descriptorType: 'field' } },
  ],
} satisfies Record<BusinessEventType, MentionData[]>)
